import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useContestarPagamento } from "@/hooks/use-mercado";
import { fmtMoedaBR } from "@/lib/formato-mercado";
import { ErroApi } from "@/lib/queryClient";
import { dataCurtaBR } from "@shared/boletim-periodo";
import { descricaoNaoRecebi } from "@shared/textos-pagamento";
import type { CompartilhamentoResumo } from "@shared/mercado";

/**
 * "Não recebi este pagamento" — o produtor contesta o que o COMPRADOR registrou.
 *
 * ⚠️ QUEM REGISTRA QUITA continua valendo: o pagamento do comprador já chegou
 * PAGO, e este diálogo é o caminho de VOLTA, escondido no "⋯" da venda. Não há
 * "confirmar" em lugar nenhum.
 *
 * O motivo é OBRIGATÓRIO — é o que o comprador lê no A pagar dele, e sem ele a
 * contestação vira uma pendência que ninguém sabe resolver. O app não manda
 * mensagem: o texto diz isso em vez de prometer aviso.
 */
export function DialogNaoRecebi({
  pagamento, compradorNome, onFechar,
}: {
  pagamento: NonNullable<CompartilhamentoResumo["pagamentoDoComprador"]>;
  compradorNome: string;
  onFechar: () => void;
}) {
  const { toast } = useToast();
  const contestar = useContestarPagamento();
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-md max-h-[90svh] overflow-y-auto" data-testid="dialog-nao-recebi">
        <DialogHeader>
          <DialogTitle>Não recebi este pagamento</DialogTitle>
          <DialogDescription>
            {compradorNome} registrou {fmtMoedaBR(pagamento.valor)} pagos em {dataCurtaBR(pagamento.data)}. Se o dinheiro
            não caiu, conte o que aconteceu: {descricaoNaoRecebi(compradorNome)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="motivo-nao-recebi">O que aconteceu</Label>
          <Textarea
            id="motivo-nao-recebi"
            rows={3}
            maxLength={500}
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder="Ex.: o PIX não caiu na conta até hoje"
            data-testid="input-motivo-nao-recebi"
          />
        </div>

        {erro && <p className="text-sm text-destructive" role="alert" data-testid="erro-nao-recebi">{erro}</p>}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="destructive"
            disabled={!motivo.trim() || contestar.isPending}
            onClick={() => {
              setErro(null);
              contestar.mutate(
                { lancamentoId: pagamento.id, motivo: motivo.trim() },
                {
                  onSuccess: () => {
                    toast({ title: "Pagamento contestado", description: `O romaneio voltou a ficar em aberto. ${compradorNome} é avisado.` });
                    onFechar();
                  },
                  onError: (e: Error) => setErro(e instanceof ErroApi && e.mensagem ? e.mensagem : e.message),
                },
              );
            }}
            data-testid="button-confirmar-nao-recebi"
          >
            {contestar.isPending ? "Registrando…" : "Registrar que não recebi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
